/**
 * Visibility view — "how agents see your site" (ADR-0110 design, ADR-0113 data).
 *
 * Phase A renders the REAL visibility score + present/missing fields
 * fetched from `GET /v1/sites/:id/visibility` (server-computed from
 * the site row + agent_card_profile). The score and field presence
 * are real data — no sample, no SAMPLE chip.
 *
 * Phase B Slice B3 (ADR-0114): the per-field "Auto-fix soon" pill on
 * missing rows becomes "Review suggestion" when the merchant's site
 * has a recent extraction with a populated `suggested_card_profile_
 * fields` arm. Clicking opens the {@link SuggestionsModal} which
 * surfaces the LLM suggestions (UNTRUSTED — rendered as inert
 * escaped text under a "Suggested by Sill's site analyzer" badge)
 * and posts the merchant's per-field selections to the typed apply
 * route `POST /v1/sites/:site_id/agent-card-profile`.
 *
 * No-auto-apply (ADR-0114 §4.4): the modal NEVER reads the
 * extraction result and writes the card; it ALWAYS goes through the
 * typed apply route. The LLM output is display data; the merchant's
 * click is the write authority.
 *
 * Naming: reference said "Foyer"; this uses "Sill" (ADR-0013).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowUpRight,
  Check,
  Pencil,
  Plus,
  RefreshCw,
  Sparkles,
  Wand2,
} from 'lucide-react';

import type {
  ProfileSuggestedCardProfileField,
  SiteComplianceValues,
  SiteId,
  VisibilityField,
  VisibilityReport,
} from '@sill/types';

import type { View } from '../../app/view-router.js';
import { AuthRateLimitError } from '@sill/types/api';

import { useApi } from '../../api/api-context.js';
import {
  APPLY_FIELD_NAMES,
  computeSuggestionContentHash,
  createAgentCardProfileApi,
  type ApplyFieldName,
} from '../../api/agent-card-profile.js';
import { updateSiteCompliance } from '../../api/site-compliance.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { useSites } from '../../app/providers.js';
import { useAgentCardProfileApply } from '../../hooks/useAgentCardProfileApply.js';
import { useC } from '../../lib/theme.js';
import { Card, EmptyStateCard, NumLabel } from '../../components/atoms/index.js';
import {
  routeFor,
  type AgentCardEditableFieldId,
} from '../shared/agent-card-field-routing.js';
import { SkillsHealthPanel } from './SkillsHealthPanel.js';
import { SuggestionsModal } from './SuggestionsModal.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";

/**
 * ADR-0127 Slice 1 — map a structured-presence visibility field id
 * (the `*_set` suffix used in the score) to the writable
 * `site_compliance` column id (the FIELD_ROUTING key). The visibility
 * report scores presence (`returns_policy_set`); the apply route
 * writes the column (`returns_policy`). Catalog-derived presence
 * (`catalog_search_eligible`) intentionally has no entry — its edit
 * surface is the Catalog view, not an inline modal.
 */
const COMPLIANCE_ROW_TO_FIELD_ID: Readonly<
  Record<string, AgentCardEditableFieldId | undefined>
> = {
  returns_policy_set: 'returns_policy',
  seller_tos_url_set: 'seller_tos_url',
  seller_privacy_policy_url_set: 'seller_privacy_policy_url',
};

type ViewMode = 'split' | 'human' | 'agent';
type LoadState = 'idle' | 'loading' | 'loaded' | 'error';

/** Header trigger's lifecycle (ADR-0114 — Visibility header extraction).
 *  Mirrors `useProfileExtraction`'s UI status enum so future callers can
 *  hoist the trigger into a shared hook without churning copy. */
type ExtractionUiStatus = 'idle' | 'running' | 'complete' | 'failed';

/** Poll cadence + ceiling for the header "Analyze my site" trigger.
 *  Matches `useProfileExtraction` (the onboarding hook) so the dashboard
 *  and the server's orphan-sweep window agree on the give-up boundary. */
const POLL_INTERVAL_MS = 2_000;
const POLL_MAX_MS = 30_000;

export interface VisibilityProps {
  /** Top-level dashboard navigation (App.tsx `setView`). Used by the
   *  structured-only field rows to jump to the Catalog view, which is
   *  the write surface for compliance + catalog-derived fields
   *  (returns_policy / ToS / privacy URL / catalog price). Named
   *  `goTo` to avoid colliding with the local `setView` ViewMode
   *  toggle (split/human/agent) inside this view. */
  goTo: (v: View) => void;
  /** Agent readiness drawer (2026-10-01): render only the field editor
   *  (field list + SuggestionsModal), without the page header, score,
   *  preview or skills panel. */
  embedded?: boolean;
  /** When opened from a specific field's row on the readiness page,
   *  scroll to and open the editor modal for just that field (in
   *  `edit` or `manual` mode depending on whether the row has a
   *  value). The id matches `AgentCardEditableFieldId` strings. */
  focusFieldId?: string | null;
}

export function Visibility({ goTo, embedded = false, focusFieldId = null }: VisibilityProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const http = useOptionalHttp();
  const { activeSite } = useSites();
  const siteId = activeSite?.site_id;

  const [report, setReport] = useState<VisibilityReport | null>(null);
  const [state, setState] = useState<LoadState>('idle');
  const [view, setView] = useState<ViewMode>('split');

  // ADR-0125 — per-field review modal state. The modal is opened
  // when the merchant clicks a per-row action on a field. The
  // founder-approved guiding principle (2026-06-18): every field has
  // exactly one clear action — Review (unset + suggestion),
  // Set manually (unset + no suggestion), Edit (already set). All
  // three open the SAME editable modal and route through the same
  // typed apply path. `reviewingFieldId` holds the single field under
  // review (prototype + ADR-0125 §Decision · Modal contract: one
  // field at a time, not batch); `reviewingMode` drives the modal's
  // copy + payload shape. The full suggestions array stays in state
  // so the content-hash on apply remains over the DISPLAYED
  // suggestion bytes (MH-4 / ADR-0114 §5.1) — the parent computes it
  // across the entire displayed set, not just the field being
  // applied. In `manual` / `edit` modes there is no displayed
  // suggestion so `source_hint` is absent.
  // ADR-0127 — widened from `ApplyFieldName` (7 card fields) to
  // `AgentCardEditableFieldId` (7 card + 3 compliance). The modal
  // dispatches the save based on the FIELD_ROUTING table; the parent's
  // `handleApply` branches on the field's `storage_home`.
  const [reviewingFieldId, setReviewingFieldId] =
    useState<AgentCardEditableFieldId | null>(null);
  const [reviewingMode, setReviewingMode] = useState<
    'review' | 'manual' | 'edit'
  >('review');
  // Live preview overlay (founder feedback #3 — Human/Agent preview
  // reactivity). Threaded from the modal's `onValueChange` hook so
  // the right-side panel renders the merchant's in-progress edit on
  // top of `fields` even before the apply lands. Cleared on modal
  // close. Untrusted content is rendered through the same JSX text-
  // node escape boundary as `fields[].value` — no new untrusted-
  // output surface.
  const [pendingPreview, setPendingPreview] = useState<
    { field_id: string; value: string } | null
  >(null);
  const [suggestions, setSuggestions] = useState<
    readonly ProfileSuggestedCardProfileField[] | null
  >(null);
  const [profileExtractionId, setProfileExtractionId] = useState<string | null>(
    null,
  );
  // The current eTag for the merchant's `agent_card_profile` row.
  // `*` means "no row yet — INSERT". On 200 we replace with the
  // server's returned eTag so the next apply chains correctly.
  const [etag, setEtag] = useState<string>('*');
  // Toast tone semantics (CLAUDE.md §4 palette):
  //   - 'lime'  → approve / success ("N fields applied")
  //   - 'coral' → reject / failure ("Couldn't analyze your site…")
  //   - 'sky'   → pending / informational (neutral; "Analysis complete —
  //     no new profile suggestions"). The empty-but-successful case is
  //     deliberately NOT coral because the run worked; the founder's
  //     core complaint across this feature was silent / misleading
  //     failure, so the message must be unmistakably distinct from an
  //     error surface (different color, different icon).
  const [toast, setToast] = useState<
    | { tone: 'lime' | 'coral' | 'sky'; copy: string }
    | null
  >(null);

  // ADR-0114 — header-driven "Analyze my site" trigger. Closes the
  // gap the founder hit: the onboarding-only trigger (now removed per
  // ADR-0050 §2.1) left already-onboarded sites with no way to re-run
  // extraction if the original onboarding pass was skipped or errored.
  // The Visibility view always has the trigger in the header so the
  // merchant can run a fresh pass any time (e.g. after editing the
  // site copy and wanting a new pass over the page).
  //
  // `extractionStatus === 'running'` disables the button + surfaces
  // the staged progress card (founder feedback #6: "polished, on-
  // brand progress indication" replacing the grey jump). The card
  // reserves layout space so the fields list below doesn't jump when
  // it appears or disappears.
  const [extractionStatus, setExtractionStatus] = useState<ExtractionUiStatus>(
    'idle',
  );
  /** Wall-clock the current run started at — drives the staged
   *  progress card (founder feedback #6). The card walks through
   *  "Reading your site → Analyzing content → Drafting suggestions"
   *  based on elapsed time so the merchant gets a sense of progress
   *  while the run takes 5-15s (longer when the scrape escalates to
   *  the proxy unblocker tier). */
  const [analyzeStartedAt, setAnalyzeStartedAt] = useState<number | null>(null);
  /** Stage index drives the staged progress card visual. Re-derives
   *  from `analyzeStartedAt` via a 500ms tick so the card feels alive
   *  without depending on the poll lifecycle. */
  const [progressStage, setProgressStage] = useState<0 | 1 | 2>(0);
  /** Polling timer handle. Held in a ref so the cleanup callback (and
   *  every transition that ends polling) can clear it without threading
   *  through state. Cleared on unmount, on site change, on completion,
   *  on failure, and on the 30s ceiling. */
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Wall-clock the current poll loop started at — for the 30s bound only.
   *  Not used for any client/server timestamp comparison. */
  const pollStartedAt = useRef<number>(0);
  /** Generation counter for poll loops. Incremented on every
   *  `beginPolling()` call AND on every `clearPoll()` call. Each tick
   *  captures the generation it was scheduled under; on resume, if the
   *  captured generation no longer matches the current one, the tick
   *  no-ops. This is the bulletproof fix for the founder's bug
   *  (2026-06-18):
   *
   *  Root cause: the previous id-set gate (`ownRunId` +
   *  `runIdsSeenRunning`) had two failure modes that BOTH produced
   *  "30s coral, zero polls visible in prod logs":
   *
   *    1. A stale tick — scheduled under a prior `beginPolling` call —
   *       could fire AFTER a new `clearPoll`/`beginPolling` sequence
   *       had reset the id-set. The stale tick would observe the row's
   *       id in NEITHER `ownRunId` nor `runIdsSeenRunning` and silently
   *       continue (no resolution), while the new poll's interval was
   *       already running. From the merchant's perspective the run had
   *       been ticked once and then nothing — the 30s ceiling on the
   *       NEW poll eventually fired coral.
   *    2. Rapid re-clicks. The `analyzeInFlight` ref blocks the second
   *       `start()` API call, but if a click landed RIGHT as the prior
   *       run's `setExtractionStatus('idle')` committed and BEFORE
   *       React re-rendered the disabled button, two `beginPolling`
   *       calls could overlap. The id-set rejected the masked run's
   *       terminal row forever → 30s ceiling.
   *
   *  The generation fence makes both impossible: only ticks whose
   *  captured generation matches the current generation do anything.
   *  Combined with the simpler "accept any terminal row after start"
   *  gate below (which the function's own prior comment admitted was
   *  correct in practice — the INSERT commits before the 202 and
   *  reads hit the primary), the poll is now robust to any tick
   *  reordering, rapid re-clicks, or stale closures. */
  const pollGeneration = useRef<number>(0);
  /** Re-entrancy guard for `handleAnalyze`. The button's `disabled`
   *  attribute is the primary guard, but the `extractionStatus` check
   *  inside the callback reads the closure-captured value and a fast
   *  double-fire can spawn two concurrent runs before React re-renders
   *  the disabled button. The ref is set synchronously the moment the
   *  callback starts and cleared in every terminal branch. */
  const analyzeInFlight = useRef<boolean>(false);
  /** Mounted flag — guard async state updates after unmount. */
  const mounted = useRef<boolean>(true);

  // Lazily build the apply-bag once per http-client instance. The
  // bag is stateless so a memoized factory is correct.
  //
  // When the http context is absent (test scaffolds that don't mount
  // `<HttpProvider />`), we fall back to a stub bag that always
  // returns `{ kind: 'unknown' }`. The view degrades to read-only —
  // no apply path — which keeps Phase-A tests passing without
  // requiring them to mount the http provider.
  const cardProfileApi = useMemo(
    () =>
      http !== null
        ? createAgentCardProfileApi({ http })
        : {
            apply: async (): Promise<{ kind: 'unknown' }> => ({
              kind: 'unknown' as const,
            }),
            getEtag: async (): Promise<{ kind: 'unknown' }> => ({
              kind: 'unknown' as const,
            }),
          },
    [http],
  );
  const {
    pending: applyPending,
    lastFailure: applyFailure,
    apply,
    clearFailure: clearApplyFailure,
  } = useAgentCardProfileApply({ api: cardProfileApi });

  /**
   * Read the latest extraction for this site and project its
   * `suggested_card_profile_fields` arm into a per-field-id map.
   * Returns null when no extraction has run or the arm is empty.
   */
  const loadSuggestions = useCallback(
    async (
      site_id: SiteId,
    ): Promise<{
      extraction_id: string;
      fields: readonly ProfileSuggestedCardProfileField[];
    } | null> => {
      const latest = await api.profile_extraction.getLatest(site_id);
      if (latest === null) return null;
      if (latest.status !== 'complete') return null;
      const arm = latest.suggested_card_profile_fields ?? [];
      if (arm.length === 0) return null;
      // Defensive: keep only fields whose `field_id` is an APPLY field
      // name. The server validator drops `name`, but a stale wire
      // could carry one — exclude it from the modal so the apply
      // payload never references a non-writable field.
      const filtered = arm.filter((f) =>
        (APPLY_FIELD_NAMES as readonly string[]).includes(f.field_id),
      );
      if (filtered.length === 0) return null;
      return {
        extraction_id: latest.profile_extraction_id,
        fields: filtered,
      };
    },
    [api],
  );

  // ---------------------------------------------------------------
  // "Analyze my site" — header trigger + polling lifecycle (ADR-0114)
  // ---------------------------------------------------------------

  /** Clear the polling interval if one is active. Safe to call multiple
   *  times. Called on every transition that ends polling AND on the
   *  cleanup of the site-change / unmount effect below.
   *
   *  Also bumps {@link pollGeneration} so any tick already in-flight
   *  (its async getLatest still pending) becomes a no-op when it
   *  resumes. Without this, an in-flight tick can land state updates
   *  after the merchant has navigated away or after a new poll has
   *  started — the founder's "30s coral with zero observed polls" bug. */
  const clearPoll = useCallback((): void => {
    if (pollTimer.current !== null) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    pollGeneration.current += 1;
  }, []);

  /** Re-fetch the visibility report after a successful extraction. The
   *  extraction itself does not write to the agent_card_profile row
   *  (that's the merchant's per-field click via the SuggestionsModal),
   *  so the score won't change here — but the per-field `present`
   *  values reflect server-side denormalization, and a fresh GET is
   *  cheap insurance against any future server-side enrichment. */
  const refreshReport = useCallback(
    async (site_id: SiteId): Promise<void> => {
      try {
        const r = await api.visibility.getReport(site_id);
        if (mounted.current) setReport(r);
      } catch {
        // Non-fatal — the existing report stays on screen; the
        // suggestions list (which is what the merchant clicks through
        // to apply) populates regardless.
      }
    },
    [api],
  );

  /** Apply a completed extraction result to local state. Mirrors the
   *  on-mount loader's shape so the per-field "Review suggestion"
   *  affordance lights up identically.
   *
   *  Returns a discriminant so callers (the user-driven `handleAnalyze`
   *  / `beginPolling` paths) can distinguish "got suggestions — open
   *  the review affordance, no toast needed" from "completed but
   *  empty — show a neutral 'analysis complete, nothing to suggest'
   *  toast." The on-mount loader does NOT toast on empty (the merchant
   *  didn't click anything; surfacing a toast there would be noisy);
   *  this discriminant is only consumed by the trigger paths. */
  const applyCompleteExtraction = useCallback(
    (
      extractionId: string,
      arm: readonly ProfileSuggestedCardProfileField[],
    ): { kind: 'with_suggestions' } | { kind: 'empty' } => {
      // Defensive: keep only fields whose `field_id` is an APPLY field
      // name. Same filter the on-mount loader applies — a stale or
      // future-schema arm with a non-writable field never flows into
      // the modal's payload.
      const filtered = arm.filter((f) =>
        (APPLY_FIELD_NAMES as readonly string[]).includes(f.field_id),
      );
      if (filtered.length === 0) {
        // Extraction completed but produced no card-profile suggestions
        // (sparse site — the Haiku pass legitimately had nothing to
        // infer for the writable fields). The previous behavior was
        // silent: state was nulled and the trigger returned to idle
        // with no visible feedback, which read as "broken" and led
        // founders to rapid re-click (overlapping runs / 30s timeout
        // → coral). The view still falls back to disabled "Auto-fix
        // soon" pills for the per-field affordance; the trigger paths
        // surface a NEUTRAL sky toast instead of silence.
        setSuggestions(null);
        setProfileExtractionId(null);
        return { kind: 'empty' };
      }
      setSuggestions(filtered);
      setProfileExtractionId(extractionId);
      return { kind: 'with_suggestions' };
    },
    [],
  );

  /** Begin the 2s-interval poll for the given site. Capped at 30s; past
   *  the cap we surface coral "Couldn't analyze your site in time" so
   *  the merchant gets an honest signal (no spinner that never
   *  resolves).
   *
   *  Pollfix (2026-06-18) — generation-fenced, accept-any-terminal.
   *  Replaces the prior v3 id-set membership gate (`ownRunId` +
   *  `runIdsSeenRunning`) which had two failure modes that BOTH
   *  produced "30s coral, zero observed GET polls" in prod:
   *
   *    1. Stale tick after `clearPoll` / new `beginPolling`. The
   *       in-flight `getLatest` promise's `.then` would run AFTER the
   *       id-set was reset; the row id was in neither set; the tick
   *       silently no-op'd while the merchant saw no progress.
   *    2. Rapid re-click overlap. The `analyzeInFlight` ref blocks a
   *       second `start()` call but state-transition races could land
   *       two `beginPolling` invocations in flight; the second wiped
   *       the first's id-set without surfacing its terminal row.
   *
   *  New shape:
   *    - {@link pollGeneration} is bumped on every `beginPolling` AND
   *      every `clearPoll`. Each tick captures the generation it was
   *      scheduled under; if the captured generation no longer
   *      matches when its async `getLatest` resolves, the tick
   *      no-ops. No stale tick can ever mutate state under a new
   *      poll or after the poll was cleared.
   *    - Terminal gate is the simplest correct rule: once `start()`
   *      has returned `running` and `beginPolling` has been called,
   *      ANY terminal (`complete`/`failed`) row from `getLatest`
   *      resolves the poll. The architectural justification (which
   *      the prior id-set comment also conceded as "correct in
   *      practice"): the API path's `createRunningRow` INSERT commits
   *      inside the tenant tx BEFORE the 202 ack returns, and reads
   *      hit the pooled primary, so the very next `getLatest` AFTER
   *      the 202 cannot return a pre-click row. The only terminal
   *      row that can land is this click's own run (or an overlapping
   *      newer run, which is strictly newer and resolves correctly).
   *
   *  This is what the existing function-level comment already admitted
   *  was correct: "accept-any-complete would also be correct in
   *  practice." We've now made that the actual implementation. */
  const beginPolling = useCallback(
    (site_id: SiteId): void => {
      clearPoll();
      pollStartedAt.current = Date.now();
      // Bump the generation NOW so the upcoming setInterval-scheduled
      // ticks and the immediate first tick all capture the SAME fresh
      // generation. Any prior in-flight `getLatest` promise resolves
      // under a stale generation and no-ops.
      pollGeneration.current += 1;
      const myGeneration = pollGeneration.current;
      const tick = async (): Promise<void> => {
        // Generation fence — top of tick. If clearPoll has been called
        // OR a newer beginPolling has overwritten us, abort. This
        // covers the rare race where a setInterval-scheduled tick
        // fires AFTER clearInterval was issued but before the runtime
        // reaped the callback queue.
        if (myGeneration !== pollGeneration.current) return;
        if (!mounted.current) {
          clearPoll();
          return;
        }
        if (Date.now() - pollStartedAt.current > POLL_MAX_MS) {
          clearPoll();
          if (!mounted.current) return;
          setExtractionStatus('failed');
          setToast({
            tone: 'coral',
            copy: "Couldn't analyze your site in time. Try again shortly.",
          });
          return;
        }
        try {
          const latest = await api.profile_extraction.getLatest(site_id);
          // Generation fence — after await. The getLatest promise may
          // have been in flight while clearPoll ran or a new poll
          // started; the response must not mutate state under a stale
          // generation. This is the bulletproof fix for the founder's
          // bug.
          if (myGeneration !== pollGeneration.current) return;
          if (!mounted.current) return;
          if (latest === null) {
            // 404 mid-poll — the feature flag flipped or the site is
            // unknown under tenancy. Stop polling and surface as failed
            // so the merchant gets an honest signal rather than a
            // spinner that never resolves.
            clearPoll();
            setExtractionStatus('failed');
            setToast({
              tone: 'coral',
              copy: "Site analysis isn't available right now. Try again shortly.",
            });
            return;
          }
          // Simple accept-any-terminal gate. See the function comment
          // for the architectural justification: the INSERT commits
          // before the 202 and reads hit the primary, so the first
          // post-202 getLatest cannot return a pre-click row. Any
          // terminal row that lands here is THIS click's run (or an
          // overlapping concurrent run, which is strictly newer).
          if (latest.status === 'complete') {
            clearPoll();
            const outcome = applyCompleteExtraction(
              latest.profile_extraction_id,
              latest.suggested_card_profile_fields ?? [],
            );
            setExtractionStatus('idle');
            if (outcome.kind === 'empty') {
              // Sparse site — Haiku returned a clean `complete` with
              // zero applicable suggestions. Surface a neutral sky
              // toast so the merchant knows the run worked (vs the
              // previous silence, which read as broken and triggered
              // re-click loops that hit the 30s timeout).
              setToast({
                tone: 'sky',
                copy: 'Analysis complete — no new profile suggestions for this page.',
              });
            }
            // outcome.kind === 'with_suggestions' → no toast; the lit-up
            // "Review suggestion" pills ARE the feedback.
            void refreshReport(site_id);
            return;
          }
          if (latest.status === 'failed') {
            clearPoll();
            setExtractionStatus('failed');
            setToast({
              tone: 'coral',
              copy: "Couldn't analyze your site. Try again shortly.",
            });
            return;
          }
          // status === 'running' — keep polling, no state mutation.
        } catch {
          // Network / 5xx / unknown mid-poll → fail-soft. Surface as
          // failed so the trigger returns to clickable and the
          // merchant can retry.
          if (myGeneration !== pollGeneration.current) return;
          clearPoll();
          if (!mounted.current) return;
          setExtractionStatus('failed');
          setToast({
            tone: 'coral',
            copy: "Couldn't reach the analyzer. Try again shortly.",
          });
        }
      };
      pollTimer.current = setInterval(() => {
        void tick();
      }, POLL_INTERVAL_MS);
      // Fire one tick immediately so a fast-completing run is observed
      // without waiting the full 2s for the first poll.
      void tick();
    },
    [api, applyCompleteExtraction, clearPoll, refreshReport],
  );

  /** Kick off a fresh extraction. The button is disabled while running
   *  so this can't reentrantly stack timers; the per-account rate limit
   *  on the server side maps to AuthRateLimitError, which we render as
   *  a friendly toast.
   *
   *  Re-entrancy: the button's `disabled` attribute is the primary
   *  guard but the `extractionStatus === 'running'` early-return below
   *  reads the closure-captured state value — a fast double-click can
   *  observe `idle` twice before React re-renders the disabled button.
   *  The `analyzeInFlight` ref is set synchronously to make double-fire
   *  impossible (a click that arrives mid-start() is a no-op). */
  const handleAnalyze = useCallback(async (): Promise<void> => {
    if (!siteId) return;
    if (analyzeInFlight.current) return;
    analyzeInFlight.current = true;
    setExtractionStatus('running');
    setAnalyzeStartedAt(Date.now());
    setProgressStage(0);
    try {
      const result = await api.profile_extraction.start(siteId as SiteId);
      if (!mounted.current) return;
      if (result === null) {
        // Feature flag off (or site unknown under tenancy). Degrade
        // gracefully — no crash, an honest message.
        setExtractionStatus('failed');
        setToast({
          tone: 'coral',
          copy: "Site analysis isn't available on this site.",
        });
        return;
      }
      if (result.status === 'complete') {
        // The mock bag short-circuits to a complete result on `start`;
        // also a defensive branch if the server ever returns 200 with
        // a fully populated row instead of the 202 ack.
        const outcome = applyCompleteExtraction(
          result.profile_extraction_id,
          result.suggested_card_profile_fields ?? [],
        );
        setExtractionStatus('idle');
        if (outcome.kind === 'empty') {
          // Same neutral surface as the poll's complete-empty branch.
          // The merchant clicked, the run worked, there was just
          // nothing to suggest — say so plainly. NOT coral.
          setToast({
            tone: 'sky',
            copy: 'Analysis complete — no new profile suggestions for this page.',
          });
        }
        void refreshReport(siteId as SiteId);
        return;
      }
      if (result.status === 'failed') {
        setExtractionStatus('failed');
        setToast({
          tone: 'coral',
          copy: "Couldn't analyze your site. Try again shortly.",
        });
        return;
      }
      // status === 'running' — transition into the poll loop.
      beginPolling(siteId as SiteId);
    } catch (err) {
      if (!mounted.current) return;
      // 429 — per-account rate limit (server enforces a cap on how
      // often a merchant can re-run extraction). Surface a friendly,
      // honest message; the trigger returns to clickable after.
      if (err instanceof AuthRateLimitError) {
        setExtractionStatus('idle');
        setToast({
          tone: 'coral',
          copy: "You've run this a lot — try again in a minute.",
        });
        return;
      }
      // Any other failure → fail-soft with an honest message. The
      // founder's original bug was silent failure here; don't repeat it.
      setExtractionStatus('failed');
      setToast({
        tone: 'coral',
        copy: "Couldn't analyze your site. Try again shortly.",
      });
    } finally {
      analyzeInFlight.current = false;
    }
  }, [
    api,
    applyCompleteExtraction,
    beginPolling,
    refreshReport,
    siteId,
  ]);

  // Unmount cleanup — guarantees no leaked setInterval if the merchant
  // navigates away mid-poll. Also runs on every site-change because the
  // site-load effect below clears + restarts the view's state.
  useEffect(() => {
    mounted.current = true;
    return (): void => {
      mounted.current = false;
      clearPoll();
    };
  }, [clearPoll]);

  // Site-change cleanup for the in-flight extraction state. The
  // existing report-load effect handles `report`, `suggestions`, and
  // `etag`; this effect resets the trigger lifecycle so a poll started
  // on the previous site doesn't bleed into the new one.
  useEffect(() => {
    clearPoll();
    setExtractionStatus('idle');
  }, [siteId, clearPoll]);

  useEffect(() => {
    if (!siteId) {
      setState('idle');
      setReport(null);
      setSuggestions(null);
      setProfileExtractionId(null);
      setEtag('*');
      return;
    }
    let cancelled = false;
    setState('loading');
    api.visibility
      .getReport(siteId as SiteId)
      .then((r) => {
        if (!cancelled) {
          setReport(r);
          setState('loaded');
        }
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    // Also fetch the latest extraction so we can decide whether to
    // surface the "Review suggestion" affordance per field. A failure
    // here is non-fatal — the view falls back to the disabled
    // "Auto-fix soon" pill.
    loadSuggestions(siteId as SiteId)
      .then((res) => {
        if (cancelled) return;
        if (res === null) {
          setSuggestions(null);
          setProfileExtractionId(null);
        } else {
          setSuggestions(res.fields);
          setProfileExtractionId(res.extraction_id);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSuggestions(null);
          setProfileExtractionId(null);
        }
      });
    // Founder feedback #6 (2026-06-18) — seed the eTag from the
    // SERVER so the first apply doesn't 412. Without this, the
    // dashboard always starts with `etag = '*'` (INSERT-only
    // semantics). If a row already exists (from a prior session, a
    // previous extraction-driven apply, onboarding writes, or any
    // other admin action) the very first sequential edit by the
    // SAME merchant produces a 412 stale-eTag — the bug the founder
    // hit when editing return_window_days "after a prior edit." The
    // GET returns `'*'` for sites with no row yet (clean INSERT
    // path stays intact); for sites with a row it returns the row's
    // current eTag so the first apply chains correctly.
    cardProfileApi
      .getEtag(siteId as SiteId)
      .then((result) => {
        if (cancelled) return;
        if (result.kind === 'ok') {
          setEtag(result.etag);
        } else {
          // 403/404/unknown — degrade to INSERT-only. If a row
          // genuinely exists we'll surface a 412 on the first apply
          // and the (now-fixed) refresh path recovers; this is the
          // same behavior as before this fix, just on the failure
          // path only.
          setEtag('*');
        }
      })
      .catch(() => {
        if (!cancelled) setEtag('*');
      });
    return () => {
      cancelled = true;
    };
  }, [api, siteId, loadSuggestions, cardProfileApi]);

  const fields = report?.fields ?? [];
  const missing = fields.filter((f) => !f.present);
  const score = report?.score ?? 0;

  /** Per-field-id index into the suggestion list — drives the pill
   *  state. A field with no suggestion stays disabled ("Auto-fix
   *  soon"); a field WITH a suggestion becomes "Review suggestion".
   *
   *  Keyed by `string` (not `ProfileFieldId`) so the lookup at render
   *  time accepts the wider `VisibilityFieldId` from `VisibilityField`
   *  (ADR-0118 Slice A widened the visibility scorer's field union to
   *  include the four structured-data categories). LLM suggestions only
   *  ever target `ProfileFieldId`, so the structured-data rows never
   *  hit and always render with the disabled "Auto-fix soon" affordance
   *  — they're set via the catalog / compliance views, not extraction. */
  const suggestionsByFieldId = useMemo(() => {
    const m = new Map<string, ProfileSuggestedCardProfileField>();
    for (const s of suggestions ?? []) {
      m.set(s.field_id, s);
    }
    return m;
  }, [suggestions]);

  /** Open the modal in `review` mode for a field with a suggestion.
   *  The parent's apply path threads the forensic `source_hint` (the
   *  content hash over the displayed suggestion set, MH-4). Review
   *  mode is only used for fields that came out of LLM extraction —
   *  i.e. card-profile fields. Compliance fields don't have an
   *  extraction pipeline, so they never enter this branch. */
  const handleOpenReview = useCallback(
    (field_id: string): void => {
      if (suggestions === null || profileExtractionId === null) return;
      if (!(APPLY_FIELD_NAMES as readonly string[]).includes(field_id)) return;
      setReviewingFieldId(field_id as AgentCardEditableFieldId);
      setReviewingMode('review');
    },
    [profileExtractionId, suggestions],
  );

  /** Open the modal in `manual` mode for an unset field WITHOUT a
   *  suggestion (founder feedback #1). Pre-fills the input empty so
   *  the merchant types their own value; routes through the FIELD_
   *  ROUTING-determined apply path with no `source_hint` (this isn't
   *  a suggestion acceptance — there was nothing to suggest).
   *
   *  ADR-0127 — also opens for unset compliance fields. The routing
   *  table determines the destination route. */
  const handleOpenManual = useCallback(
    (field_id: string): void => {
      if (routeFor(field_id) === undefined) return;
      setReviewingFieldId(field_id as AgentCardEditableFieldId);
      setReviewingMode('manual');
    },
    [],
  );

  /** Open the modal in `edit` mode for an already-set field (founder
   *  feedback #4). Pre-fills the input with the current card value
   *  so the merchant can revise it; routes through the same apply
   *  path with no `source_hint`.
   *
   *  ADR-0127 — also opens for already-set compliance fields. The
   *  routing table determines the destination route. */
  const handleOpenEdit = useCallback(
    (field_id: string): void => {
      if (routeFor(field_id) === undefined) return;
      setReviewingFieldId(field_id as AgentCardEditableFieldId);
      setReviewingMode('edit');
    },
    [],
  );

  // When the agent-readiness drawer opens us with a `focusFieldId`,
  // open the per-field editor automatically. We prefer `edit` for an
  // already-set field (keeps the current value in the input), fall
  // back to `review` when a suggestion is available, and `manual` as
  // the last resort. The effect runs once per `focusFieldId` change
  // and only while the report has loaded so we can read `fields`.
  useEffect(() => {
    if (focusFieldId === null || focusFieldId === undefined) return;
    if (state !== 'loaded') return;
    if (routeFor(focusFieldId) === undefined) return;
    const field = report?.fields.find((f) => f.id === focusFieldId) ?? null;
    const hasValue = field !== null && typeof field.value === 'string' && field.value.length > 0;
    const hasSuggestion = suggestions !== null
      && suggestions.some((s) => s.field_id === focusFieldId);
    setReviewingFieldId(focusFieldId as AgentCardEditableFieldId);
    setReviewingMode(hasValue ? 'edit' : hasSuggestion ? 'review' : 'manual');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusFieldId, state]);

  const handleCloseModal = useCallback((): void => {
    setReviewingFieldId(null);
    setPendingPreview(null);
    // ADR-0127 — clear the compliance failure surface on close so a
    // stale banner doesn't reappear when the merchant re-opens a
    // different field. The card-profile failure clears on the next
    // apply via the hook's `setLastFailure(null)` at apply start;
    // this is the compliance-path mirror.
    setComplianceFailure(null);
  }, []);

  /** Modal `onValueChange` hook — receives the merchant's in-progress
   *  edit on every keystroke so the right-side Human / Agent panel
   *  can overlay it on top of `fields`. The untrusted content is
   *  rendered through the same React text-node escape boundary as
   *  `fields[].value`, so this opens no new XSS surface. */
  const handleLivePreview = useCallback(
    (field_id: string, value: string): void => {
      setPendingPreview({ field_id, value });
    },
    [],
  );

  /** Re-fetch suggestions + the CURRENT real eTag (412 stale path).
   *  The modal stays open during the refresh so the merchant doesn't
   *  lose their selections.
   *
   *  Security review Minor 1 (2026-06-16) — DO NOT reset to `'*'`
   *  here. `'*'` is INSERT-only semantics; a 412 happens precisely
   *  because a row already exists, so the next apply would 412 again
   *  forever (permanent stalemate; merchant can't recover without a
   *  full page reload). Instead, GET the row's current eTag and
   *  thread it back into local state so the retry apply targets the
   *  existing row. If the GET returns `present: false` (the row was
   *  deleted between the apply and the refresh — vanishingly rare),
   *  fall through to `'*'` so the next apply re-INSERTs.
   *
   *  Founder feedback #6 (2026-06-18) — three additional fixes that
   *  made the button feel dead before:
   *
   *    1. CLEAR the stale-eTag banner immediately via the hook's new
   *       `clearFailure()` reset. The banner is gated on
   *       `applyFailure?.kind === 'concurrency_mismatch'`; before
   *       this fix the banner only cleared on the NEXT apply call,
   *       so a successful refresh appeared as "the button did
   *       nothing." Now the merchant sees the banner vanish on
   *       click, which is the unambiguous "the refresh worked"
   *       signal.
   *    2. Do NOT close the modal when `loadSuggestions` returns null.
   *       The previous handler called `setReviewingFieldId(null)` on
   *       any null result, which silently dismissed the modal on
   *       Edit / Manual mode (those modes don't need a suggestion).
   *       Only the review-mode + missing-suggestion case warrants a
   *       close — those modes already filter to "open only when a
   *       suggestion exists," so if the suggestion vanished mid-
   *       session the modal can no longer make a coherent claim.
   *    3. On Edit / Manual mode the suggestion arm is irrelevant —
   *       we DO NOT reset `suggestions` / `profileExtractionId` on
   *       the null path because doing so would lose the per-field
   *       Review affordance for unrelated fields the merchant may
   *       want to act on after closing this modal.
   */
  const handleRefreshSuggestions = useCallback(async (): Promise<void> => {
    if (!siteId) return;
    const etagResult = await cardProfileApi.getEtag(siteId as SiteId);
    if (etagResult.kind === 'ok') {
      // The GET returned the row's current eTag (or `'*'` when the
      // row genuinely does not exist). Thread it into local state so
      // the retry apply targets the existing row instead of a fresh
      // INSERT.
      setEtag(etagResult.etag);
    } else {
      // The recovery GET failed (403/404/unknown). Reset to `'*'` —
      // the apply will surface a clean error mode (404 if the site
      // really is gone, 412 again if a row exists). This is the
      // pre-fix behavior, but only on the failure path; the happy
      // path is now a real eTag.
      setEtag('*');
    }
    const res = await loadSuggestions(siteId as SiteId);
    if (res === null) {
      // Founder feedback #6 — only close + clear suggestions when the
      // modal is in REVIEW mode (which strictly requires a displayed
      // suggestion). Edit / Manual modes don't care about the
      // suggestion arm, so we leave their modal open and clear only
      // the stale-eTag banner. Otherwise the merchant would perceive
      // Refresh as "nothing happened" because the modal vanished
      // silently with no toast.
      if (reviewingMode === 'review') {
        setSuggestions(null);
        setProfileExtractionId(null);
        setReviewingFieldId(null);
      }
      clearApplyFailure();
      return;
    }
    setSuggestions(res.fields);
    setProfileExtractionId(res.extraction_id);
    // Banner clears AFTER state writes so the modal re-renders fresh.
    clearApplyFailure();
  }, [cardProfileApi, clearApplyFailure, loadSuggestions, reviewingMode, siteId]);

  // ADR-0127 — compliance-path local state. The compliance route
  // doesn't go through `useAgentCardProfileApply` (no eTag, different
  // failure taxonomy), so the parent tracks pending + failure locally
  // for the modal's busy + inline-error rendering. The modal already
  // reads its `pending` and `applyFailure` props the same way for
  // either path; we OR-combine the two sources below at the render
  // site so the existing UX is unchanged from the modal's perspective.
  const [compliancePending, setCompliancePending] = useState<boolean>(false);
  const [complianceFailure, setComplianceFailure] = useState<
    | { kind: 'forbidden' | 'not_found' | 'validation' | 'unknown'; server_reason?: string }
    | null
  >(null);

  /** Apply a single field's merchant-edited value (ADR-0125 — per-
   *  field apply; ADR-0127 — dual-edit dispatch).
   *
   *  The modal forwards `fields: { [field_id]: edited_value }` for one
   *  writable field. We look up the field's route via FIELD_ROUTING
   *  and dispatch to one of two backends:
   *
   *    - `agent_card_profile` → POST /v1/sites/:id/agent-card-profile
   *      with `If-Match` (eTag). Carries `source_hint` in review mode.
   *      Result-kind branches mirror the prior ADR-0125 flow.
   *
   *    - `site_compliance` → PUT /v1/sites/:id/compliance (partial
   *      merge, last-write-wins; ADR-0127 §4). No eTag. Carries no
   *      `source_hint` (no extraction pipeline). After a successful
   *      save, re-fetches the visibility report so the row + score
   *      update.
   *
   *  The dual surface lives ONLY at this dispatch boundary — both
   *  routes are the ones already on the wire today. ADR-0118's
   *  single-storage-home invariant is preserved.
   */
  const handleApply = useCallback(
    async (args: {
      fields: Partial<Record<AgentCardEditableFieldId, string>>;
    }): Promise<void> => {
      if (!siteId) return;
      // The modal sends exactly one field per apply (per-field
      // contract from ADR-0125). Pull it out so we can look up the
      // route. If the payload is empty or covers multiple fields, the
      // routing decision is ambiguous — fall back to the legacy card-
      // profile path (defensive; the modal's contract makes this
      // unreachable in practice).
      const fieldNames = Object.keys(args.fields) as AgentCardEditableFieldId[];
      const primaryField = fieldNames[0];
      const route =
        primaryField !== undefined ? routeFor(primaryField) : undefined;

      // ───────────────────────────────────────────────────────────
      // site_compliance dispatch (ADR-0127 Slice 1)
      // ───────────────────────────────────────────────────────────
      if (
        primaryField !== undefined
        && route !== undefined
        && route.storage_home === 'site_compliance'
      ) {
        if (http === null) {
          // The HTTP context wasn't mounted (test scaffolds without
          // the provider, or a render before the provider lands).
          // Surface a clean unknown so the modal closes cleanly.
          setToast({
            tone: 'coral',
            copy: "Couldn't reach the compliance service. Try again shortly.",
          });
          return;
        }
        setCompliancePending(true);
        setComplianceFailure(null);
        // The compliance route accepts string|null per field; we
        // forward the merchant's edited value verbatim. An empty
        // string is treated as a clear by the route (for ToS / privacy
        // URLs) or a 400 (for returns_policy, which is non-empty
        // free text). The server validators are the floor; the
        // client-side validation in the modal already blocks the
        // obvious shape errors.
        const editedValue = args.fields[primaryField] ?? '';
        const update: Record<string, string | null> = {
          [primaryField]: editedValue.length === 0 ? null : editedValue,
        };
        const result = await updateSiteCompliance(
          http,
          siteId as SiteId,
          update,
        );
        setCompliancePending(false);
        if (result.kind === 'ok') {
          // Re-fetch the visibility report so the row + score update.
          // No eTag to thread; the next save runs through the same
          // partial-merge PUT path.
          try {
            const fresh = await api.visibility.getReport(siteId as SiteId);
            if (mounted.current) setReport(fresh);
          } catch {
            // Non-fatal — the row stays on screen until the next mount.
          }
          if (!mounted.current) return;
          setReviewingFieldId(null);
          setPendingPreview(null);
          setToast({
            tone: 'lime',
            copy: '1 field applied to your compliance settings.',
          });
          return;
        }
        if (result.kind === 'forbidden') {
          setReviewingFieldId(null);
          setPendingPreview(null);
          setToast({
            tone: 'coral',
            copy: "You don't have permission to edit this site's compliance.",
          });
          return;
        }
        if (result.kind === 'not_found') {
          setReviewingFieldId(null);
          setPendingPreview(null);
          setToast({
            tone: 'coral',
            copy: 'Site not found. Refresh the page and try again.',
          });
          return;
        }
        if (result.kind === 'validation') {
          // Stay open — the modal renders the inline 400 banner via
          // `applyFailure`. We re-shape the result into the modal's
          // discriminator union (matches the agent-card-profile path).
          setComplianceFailure({
            kind: 'validation',
            ...(result.server_reason !== undefined
              ? { server_reason: result.server_reason }
              : {}),
          });
          return;
        }
        // unknown — network / 5xx / malformed.
        setToast({
          tone: 'coral',
          copy: "Couldn't apply this change. Try again shortly.",
        });
        return;
      }

      // ───────────────────────────────────────────────────────────
      // agent_card_profile dispatch (ADR-0125, unchanged)
      // ───────────────────────────────────────────────────────────
      // Founder feedback (2026-06-18): manual / edit mode applies do
      // NOT carry a `source_hint` because there's no displayed
      // suggestion to attribute. Only `review` mode threads the
      // forensic content hash (MH-4 / ADR-0114 §5.1). The presence
      // of a hash payload is therefore the discriminator: when the
      // modal is in review mode AND suggestions are loaded, we
      // compute + thread the hash; otherwise the apply is a plain
      // merchant-typed write.
      const isReview =
        reviewingMode === 'review'
        && suggestions !== null
        && profileExtractionId !== null;
      const source_hint = isReview
        ? {
            profile_extraction_id: profileExtractionId,
            // MH-4: hash over the DISPLAYED suggestions (the entire
            // shown set, not just the field being applied). The hash
            // is stable across edits: typing in the input does NOT
            // change this hash; it changes only when the underlying
            // suggestion bytes change (e.g. a refresh that swapped
            // the suggestion set).
            suggestion_content_hash:
              await computeSuggestionContentHash(suggestions),
          }
        : undefined;
      // Narrow the apply input to ApplyFieldName — the agent-card-
      // profile route only accepts the seven card fields. The routing
      // table guarantees we only land here for card fields, so the
      // cast is safe.
      const cardFields = args.fields as Partial<Record<ApplyFieldName, string>>;
      const result = await apply({
        site_id: siteId as SiteId,
        etag,
        fields: cardFields,
        ...(source_hint !== undefined ? { source_hint } : {}),
      });
      if (result.kind === 'ok') {
        // Founder feedback #3 (reactivity). The server's authoritative
        // visibility report comes back inside `result.visibility`;
        // committing it here is what makes the Human / Agent preview
        // panels reflect the edit instantly (the panels read
        // `fields = report?.fields` and re-render on the new array
        // identity). The `pendingPreview` overlay (the live-preview
        // before apply) is cleared so the panels switch from the
        // typed-but-not-saved overlay to the server-confirmed value.
        setReport(result.visibility);
        setEtag(result.etag);
        setReviewingFieldId(null);
        setPendingPreview(null);
        const n = Object.keys(args.fields).length;
        setToast({
          tone: 'lime',
          copy: `${n} ${n === 1 ? 'field' : 'fields'} applied to your agent card.`,
        });
        return;
      }
      if (result.kind === 'concurrency_mismatch') {
        // Stay open — the modal renders its own stale-eTag banner +
        // a Refresh button. The merchant clicks it to load fresh
        // suggestions, then re-tries.
        return;
      }
      if (result.kind === 'forbidden') {
        setReviewingFieldId(null);
        setPendingPreview(null);
        setToast({
          tone: 'coral',
          copy: "You don't have permission to edit this site's profile.",
        });
        return;
      }
      if (result.kind === 'not_found') {
        setReviewingFieldId(null);
        setPendingPreview(null);
        setToast({
          tone: 'coral',
          copy: 'Site not found. Refresh the page and try again.',
        });
        return;
      }
      if (result.kind === 'validation') {
        // Stay open — the modal renders the inline 400 banner from
        // its `applyFailure` prop.
        return;
      }
      if (result.kind === 'unauthorized') {
        setReviewingFieldId(null);
        setPendingPreview(null);
        // The http-client's on_unauthorized has already fired —
        // routing to login is the provider's job. We just close.
        return;
      }
      // Network / 5xx / unknown.
      setToast({
        tone: 'coral',
        copy: "Couldn't apply suggestions. Try again shortly.",
      });
    },
    [
      api,
      apply,
      etag,
      http,
      profileExtractionId,
      reviewingMode,
      siteId,
      suggestions,
    ],
  );

  // Auto-dismiss toasts after 4 seconds so the surface doesn't pin.
  useEffect(() => {
    if (toast === null) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Founder feedback #6 — staged progress card. While the scan is
  // running, advance the visual stage on a 500ms tick so the card
  // feels alive even when the network is quiet (Haiku runs are
  // usually 3-8s; the proxy unblocker tier pushes to 15s). The
  // boundaries are tuned to the prod observed timing (median first
  // GET poll at ~1.2s; complete typically by 6s). When the run ends
  // we reset to stage 0 so a re-click starts fresh.
  useEffect(() => {
    if (extractionStatus !== 'running' || analyzeStartedAt === null) {
      setProgressStage(0);
      return;
    }
    const tick = (): void => {
      const elapsed = Date.now() - analyzeStartedAt;
      if (elapsed < 2_000) setProgressStage(0);
      else if (elapsed < 5_500) setProgressStage(1);
      else setProgressStage(2);
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [extractionStatus, analyzeStartedAt]);

  return (
    <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10'}>
      {!embedded ? (
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <NumLabel n={1}>Visibility · how agents see your site</NumLabel>
          <h1 style={{ fontFamily: serifFont, fontSize: 'clamp(36px, 6vw, 52px)', lineHeight: 1, color: C.text, letterSpacing: -1.5 }}>
            What agents <em>actually read</em>.
          </h1>
          {/* Inline status line while a fresh extraction is mid-flight.
              Mirrors `apps/dashboard/src/views/onboarding/SkillsList.tsx`
              — same testid, same spinning RefreshCw, same copy idiom.
              The SuggestionsModal (the only surface that renders LLM
              `value` / `rationale` text) stays the security boundary;
              this status line is plain Sill copy, not extraction output.
              The block reserves a fixed min-height (founder feedback
              #6) so toggling between running / idle does NOT shift
              the layout below — the staged progress card slides into
              already-reserved space rather than nudging the score
              card. */}
          <div className="mt-3" style={{ minHeight: 18 }}>
            {extractionStatus === 'running' ? (
              <div
                className="flex items-center gap-2"
                style={{
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.muted,
                }}
                data-testid="profile-extraction-running"
              >
                <RefreshCw
                  size={11}
                  style={{ animation: 'spin 1.4s linear infinite' }}
                />
                Analyzing your site…
              </div>
            ) : null}
          </div>
        </div>
        {siteId ? (
          <button
            type="button"
            onClick={() => void handleAnalyze()}
            disabled={extractionStatus === 'running'}
            aria-label="Analyze my site"
            data-testid="visibility-analyze-trigger"
            className="flex items-center gap-2 px-3.5 py-2"
            style={{
              background: C.limeDim,
              color: C.limeFg,
              border: `1px solid rgba(217,255,63,0.3)`,
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: 500,
              borderRadius: 2,
              cursor: extractionStatus === 'running' ? 'not-allowed' : 'pointer',
              opacity: extractionStatus === 'running' ? 0.55 : 1,
            }}
          >
            <Sparkles size={13} />
            {extractionStatus === 'running' ? 'Analyzing…' : 'Analyze my site'}
          </button>
        ) : null}
      </div>
      ) : null}

      {!siteId ? (
        <Card>
          <EmptyStateCard label="No active site" hint="Pick a site to see how agents read it." />
        </Card>
      ) : state === 'loading' || state === 'idle' ? (
        <Card>
          <EmptyStateCard label="Loading visibility" hint="Computing how complete your agent-readable profile is…" />
        </Card>
      ) : state === 'error' ? (
        <Card>
          <EmptyStateCard label="Visibility unavailable" hint="We couldn't load your visibility report. Try again shortly." />
        </Card>
      ) : (
        <div className="flex flex-col md:flex-row gap-6 mb-6">
          <div className="flex-1 min-w-0">
            {!embedded ? (
            <Card className="p-6">
              <div className="flex items-center gap-6">
                <ScoreGauge score={score} />
                <div className="flex-1">
                  <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 4 }}>VISIBILITY SCORE</div>
                  <div style={{ fontFamily: serifFont, fontSize: '20px', color: C.text, letterSpacing: -0.4, lineHeight: 1.2 }}>
                    {missing.length === 0 ? "You're fully agent-ready." : `${missing.length} field${missing.length === 1 ? '' : 's'} short of perfect.`}
                  </div>
                  <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>Computed from what your agent card actually exposes.</div>
                </div>
              </div>
            </Card>
            ) : null}

            {/* Staged progress card (founder feedback #6). Sits
                between the score card and the fields list while a
                scan is running so the merchant gets a lime-accented,
                on-brand sense of progress through the three stages
                ("Reading your site → Analyzing content → Drafting
                suggestions"). The card uses the same surface tier +
                border + typography idioms as the rest of the view;
                no shadows. It's wrapped in a min-height container
                that reserves layout space so the fields card below
                doesn't jump when the progress card appears or
                disappears. */}
            {/* Slot for the staged progress card. The slot itself
                renders no chrome so the layout flows naturally with
                the score / fields cards; when `extractionStatus ===
                'running'` it inflates with the lime-accented
                progress card. The `data-running` attribute is a
                stable surface for regression tests asserting the
                layout-shift fix. */}
            <div
              data-testid="scan-progress-slot"
              data-running={extractionStatus === 'running' ? 'true' : 'false'}
            >
              {extractionStatus === 'running' ? (
                <ScanProgressCard stage={progressStage} />
              ) : null}
            </div>

            <Card className="mt-5">
              <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2 }}>FIELDS · {fields.length} TOTAL</span>
              </div>
              {/* ADR-0118 Slice A — denominator changed from 42 across 7
                  fields to 66 across 11 fields, adding four credits for
                  the merchant's compliance + catalog data. A site whose
                  absolute score dipped after this rollout earned the
                  same points; the score is a percentage of a larger
                  total. One-line muted note so a returning merchant
                  understands the shift without dressing it up. */}
              {!embedded ? (
              <div
                className="px-4 py-2.5"
                style={{
                  borderBottom: `1px solid ${C.borderSoft}`,
                  background: C.surface2,
                  fontFamily: sansFont,
                  fontSize: 11,
                  color: C.muted,
                  lineHeight: 1.5,
                }}
                data-testid="visibility-rollout-note"
              >
                Scored out of 100 across 11 fields — including four new credits for compliance + catalog data.
              </div>
              ) : null}
              {fields.map((f, i) => {
                // Founder feedback (2026-06-18) — every field has
                // exactly one clear action. ADR-0127 widens the set of
                // editable rows: the seven card-profile fields stay
                // editable (existing ADR-0125 path), and the three
                // compliance presence rows (`returns_policy_set` /
                // `seller_tos_url_set` / `seller_privacy_policy_url_set`)
                // become inline-editable too. The fourth structured
                // row (`catalog_search_eligible`) remains a score-only
                // signal with the "Edit in Catalog" jump (its edit
                // surface is "upload a catalog / connect Shopify,"
                // not a single-field modal).
                const isCardApplyable = (
                  APPLY_FIELD_NAMES as readonly string[]
                ).includes(f.id);
                const complianceFieldId = COMPLIANCE_ROW_TO_FIELD_ID[f.id];
                const isComplianceApplyable = complianceFieldId !== undefined;
                const isApplyable =
                  isCardApplyable || isComplianceApplyable;
                const hasSuggestion = suggestionsByFieldId.has(f.id);
                // ADR-0127 — pre-fill source for compliance rows.
                // Card-profile rows pre-fill from `f.value` (which the
                // visibility report already echoes for set rows); the
                // three compliance rows pre-fill from the visibility
                // report's new `site_compliance_values` block. The
                // mapping is deterministic — there's exactly one
                // backing column per `_set` presence id.
                const editableFieldId: AgentCardEditableFieldId | null =
                  isCardApplyable
                    ? (f.id as AgentCardEditableFieldId)
                    : complianceFieldId ?? null;
                return (
                <div key={f.id} className="flex items-start gap-3 p-4" style={{ borderBottom: i < fields.length - 1 ? `1px solid ${C.borderSoft}` : 'none' }}>
                  {f.present ? (
                    <Check size={14} color={C.limeFg} className="mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertCircle size={14} color={C.amberFg} className="mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500, marginBottom: 2 }}>{f.label}</div>
                    <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{f.present ? f.value : 'Not set'}</div>
                  </div>
                  {isApplyable && f.present && editableFieldId !== null ? (
                    // Founder feedback #4 — set fields previously had
                    // NO action. Add an Edit affordance that opens the
                    // same modal pre-filled with the current value so
                    // the merchant can revise an already-set card
                    // value and re-apply through the typed apply
                    // route.
                    //
                    // ADR-0127 — compliance rows ALSO surface the Edit
                    // affordance when the underlying column is set.
                    // The pre-fill source is the visibility report's
                    // `site_compliance_values` block (resolved at the
                    // modal render site below).
                    <button
                      type="button"
                      onClick={() => handleOpenEdit(editableFieldId)}
                      aria-label={`Edit ${f.label}`}
                      data-testid={`edit-field-${f.id}`}
                      className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1"
                      style={{
                        background: C.surface2,
                        color: C.text,
                        border: `1px solid ${C.border}`,
                        fontFamily: sansFont,
                        fontSize: 11,
                        fontWeight: 500,
                        borderRadius: 2,
                        cursor: 'pointer',
                      }}
                    >
                      <Pencil size={10} /> Edit
                    </button>
                  ) : isCardApplyable && !f.present && hasSuggestion ? (
                    <button
                      type="button"
                      onClick={() => handleOpenReview(f.id)}
                      aria-label={`Review suggestion for ${f.label}`}
                      data-testid={`review-suggestion-${f.id}`}
                      className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1"
                      style={{
                        background: C.limeDim,
                        color: C.limeFg,
                        border: `1px solid rgba(217,255,63,0.3)`,
                        fontFamily: sansFont,
                        fontSize: 11,
                        fontWeight: 500,
                        borderRadius: 2,
                        cursor: 'pointer',
                      }}
                    >
                      <Wand2 size={10} /> Review
                    </button>
                  ) : isApplyable && !f.present && editableFieldId !== null ? (
                    // Founder feedback #1 — unset fields with NO
                    // suggestion previously rendered a disabled
                    // "Auto-fix soon" pill that promised a nonexistent
                    // feature. Replaced with a real "Set manually"
                    // action that opens the same modal with an EMPTY
                    // input so the merchant types the value and
                    // applies it through the normal path.
                    //
                    // ADR-0127 — compliance rows ALSO surface the Set
                    // manually affordance when the underlying column
                    // is unset. Compliance rows do NOT have an
                    // extraction-suggestion pipeline, so the Review
                    // branch above never lights up for them; they go
                    // straight to Set manually.
                    <button
                      type="button"
                      onClick={() => handleOpenManual(editableFieldId)}
                      aria-label={`Set ${f.label} manually`}
                      data-testid={`set-manually-${f.id}`}
                      className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1"
                      style={{
                        background: C.surface2,
                        color: C.text,
                        border: `1px solid ${C.border}`,
                        fontFamily: sansFont,
                        fontSize: 11,
                        fontWeight: 500,
                        borderRadius: 2,
                        cursor: 'pointer',
                      }}
                    >
                      <Plus size={10} /> Set manually
                    </button>
                  ) : !f.present ? (
                    // Catalog-derived structured rows (ADR-0118 Slice
                    // A) remain score-only — their edit surface is
                    // "upload a catalog or connect Shopify," not a
                    // single-field modal save. After ADR-0127, this
                    // branch is reached only by `catalog_search_eligible`
                    // (the compliance rows fall into the editable
                    // branches above). The jump destination is the
                    // Catalog view via the top-level `goTo` nav prop;
                    // a deep-link to the relevant sub-section is a
                    // future nice-to-have.
                    <button
                      type="button"
                      onClick={() => goTo('catalog')}
                      aria-label={`Edit ${f.label} in Products`}
                      data-testid={`edit-in-catalog-${f.id}`}
                      className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1"
                      style={{
                        background: C.surface2,
                        color: C.text,
                        border: `1px solid ${C.border}`,
                        fontFamily: sansFont,
                        fontSize: 11,
                        fontWeight: 500,
                        borderRadius: 2,
                        cursor: 'pointer',
                      }}
                    >
                      Edit in Products <ArrowUpRight size={10} />
                    </button>
                  ) : null}
                </div>
                );
              })}
            </Card>
          </div>

          {!embedded ? (
          <div className="flex-1 min-w-0">
            <Card>
              <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-2" style={{ borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2 }}>HOW YOUR SITE PRESENTS</span>
                <div className="flex items-center gap-1">
                  {([{ id: 'split', l: 'Both' }, { id: 'human', l: 'Human' }, { id: 'agent', l: 'Agent' }] as const).map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setView(v.id)}
                      className="px-2.5 py-1"
                      style={{ fontFamily: monoFont, fontSize: 10, color: view === v.id ? C.text : C.muted, background: view === v.id ? C.surface2 : 'transparent', borderRadius: 2 }}
                    >
                      {v.l.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div className={`grid ${view === 'split' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                {(view === 'split' || view === 'human') && (
                  <HumanView fields={fields} pendingPreview={pendingPreview} />
                )}
                {(view === 'split' || view === 'agent') && (
                  <AgentView fields={fields} pendingPreview={pendingPreview} />
                )}
              </div>
            </Card>
          </div>
          ) : null}
        </div>
      )}

      {/* ADR-0129 §2.5 — Skills health panel. Separate guidance
          section, NOT folded into the numeric visibility score
          (deferred per ADR-0129 §2.6). Data source is the same
          `GET /v1/skills` Settings uses; the route's `SkillWithStatus`
          carries `status` + `gating_reason` derived from the SAME
          backing signals the signed card uses. The merchant reads
          "how to maximize your store for agents" — what's live, what's
          enabled-but-not-live, what's coming soon, and the precise
          next step for each non-live row. */}
      {siteId && state === 'loaded' && !embedded ? (
        <SkillsHealthPanel site_id={siteId as SiteId} goTo={goTo} />
      ) : null}

      {/* Per-field editable modal (ADR-0125, founder feedback 2026-
          06-18). The same modal serves THREE entry points — every
          field has exactly one clear action:
            - Review  → unset + suggestion exists (LLM-attributed)
            - Set manually → unset + no suggestion
            - Edit → already set
          All three open this modal, the merchant edits, and the apply
          routes through `handleApply` → POST /v1/sites/:id/agent-
          card-profile. Only `review` mode threads the forensic
          `source_hint` (MH-4); `manual` / `edit` synthesize a
          placeholder suggestion so the modal's editable input has
          something to pre-fill with. Render-boundary HTML escaping +
          dir="auto" RTL/CJK safety + no-auto-apply (MH-3) live in the
          modal + this view's `handleApply`. */}
      {reviewingFieldId !== null
        && (() => {
          // Resolve the displayed suggestion for the open field.
          // In `review` mode this is the LLM suggestion; in `manual`
          // / `edit` it's a synthesized placeholder so the modal's
          // input has a baseline value to seed from. The synthesized
          // shape is never sent in any apply payload (the parent's
          // `handleApply` only threads `source_hint` when
          // `reviewingMode === 'review'`).
          //
          // ADR-0127 — compliance fields are looked up from
          // `site_compliance_values` (the visibility report's new
          // block), NOT from `fields[].value` (which scores by `_set`
          // presence id and does not carry the raw column value).
          const route = routeFor(reviewingFieldId);
          const isCompliance =
            route !== undefined && route.storage_home === 'site_compliance';
          let current_value: string | null;
          if (isCompliance) {
            const v =
              report?.site_compliance_values?.[
                reviewingFieldId as keyof SiteComplianceValues
              ];
            current_value = typeof v === 'string' && v.length > 0 ? v : null;
          } else {
            const currentField = fields.find((vf) => vf.id === reviewingFieldId);
            current_value = currentField?.value ?? null;
          }
          const reviewSuggestion =
            suggestions?.find((x) => x.field_id === reviewingFieldId) ?? null;
          // Defensive: review mode must have a real suggestion. If
          // the suggestion list went stale (extraction expired mid-
          // session), fall through silently — the field still has its
          // Set manually / Edit affordances.
          if (reviewingMode === 'review' && reviewSuggestion === null) return null;
          let s: ProfileSuggestedCardProfileField;
          if (reviewingMode === 'review' && reviewSuggestion !== null) {
            s = reviewSuggestion;
          } else if (reviewingMode === 'edit') {
            // Synthesize from the current value so the input pre-fills
            // with what the merchant has today. For compliance fields
            // this comes from `site_compliance_values`; for card
            // fields from the visibility report's per-field value.
            s = {
              field_id: reviewingFieldId as ApplyFieldName,
              value: current_value ?? '',
              rationale: '',
            };
          } else {
            // `manual` — empty input. The merchant types from scratch.
            s = {
              field_id: reviewingFieldId as ApplyFieldName,
              value: '',
              rationale: '',
            };
          }
          // Server-known source URL: use the active site's domain
          // (the only URL we both know on the dashboard side AND
          // can safely render without LLM-emitted citations). Per
          // ADR-0125 §Decision, this is the safe inert-text
          // substitute for the prototype's hallucinable "RAW QUOTE
          // · line N" panel. Same-host gating would let us link
          // this later; today it's inert text only.
          const domain = activeSite?.domain ?? '';
          const source_page_url =
            domain.length > 0 ? `https://${domain}/` : 'your site';
          // The `profileExtractionId` prop is required by the modal's
          // ts signature; in `manual` / `edit` mode it's ignored by
          // the parent's `handleApply` (no `source_hint` is sent).
          // We pass an empty string when no extraction has run yet
          // (manual mode on a fresh site) — the modal's render
          // surface doesn't read it.
          //
          // ADR-0127 — the modal's `pending` + `applyFailure` props
          // OR-combine the card-profile and compliance state surfaces
          // so the existing modal UX (busy button, inline 400 banner)
          // works identically for both dispatch paths.
          const modalPending = applyPending || compliancePending;
          const mergedFailure =
            applyFailure ?? complianceFailure ?? null;
          return (
            <SuggestionsModal
              open={true}
              onClose={handleCloseModal}
              suggestion={s}
              mode={reviewingMode}
              source_page_url={source_page_url}
              current_value={current_value}
              profileExtractionId={profileExtractionId ?? ''}
              etag={etag}
              pending={modalPending}
              onApply={handleApply}
              onRefreshRequested={() => void handleRefreshSuggestions()}
              onValueChange={handleLivePreview}
              {...(mergedFailure !== null ? { applyFailure: mergedFailure } : {})}
            />
          );
        })()}

      {/* Toast surface. Tones:
            - lime  → apply success ("N fields applied")
            - coral → failure ("Couldn't analyze your site…")
            - sky   → neutral / informational ("Analysis complete —
              no new profile suggestions for this page.") — the
              empty-but-successful trigger outcome, deliberately
              distinct from coral so the merchant reads it as
              "it worked, just nothing to suggest" instead of
              "broken" (which was the silent-failure UX that drove
              re-click loops). Auto-dismisses after 4s. */}
      {toast !== null && (
        <div
          className="fixed bottom-6 right-6 z-[200] flex items-center gap-2 px-4 py-3"
          role="status"
          data-testid={`visibility-toast-${toast.tone}`}
          style={{
            background:
              toast.tone === 'lime'
                ? C.limeDim
                : toast.tone === 'sky'
                  ? C.skyDim
                  : C.coralDim,
            color:
              toast.tone === 'lime'
                ? C.limeFg
                : toast.tone === 'sky'
                  ? C.skyFg
                  : C.coralFg,
            border: `1px solid ${
              toast.tone === 'lime'
                ? 'rgba(217,255,63,0.3)'
                : toast.tone === 'sky'
                  ? 'rgba(127,184,255,0.3)'
                  : 'rgba(255,107,71,0.3)'
            }`,
            borderRadius: 2,
            fontFamily: sansFont,
            fontSize: 13,
            fontWeight: 500,
            maxWidth: 360,
          }}
        >
          {toast.tone === 'lime' ? (
            <Check size={14} />
          ) : toast.tone === 'sky' ? (
            <Sparkles size={14} />
          ) : (
            <AlertCircle size={14} />
          )}
          <span>{toast.copy}</span>
        </div>
      )}
    </div>
  );
}

function ScoreGauge({ score }: { score: number }): JSX.Element {
  const C = useC();
  const r = 50;
  const circ = 2 * Math.PI * r;
  return (
    <div className="relative flex items-center justify-center" style={{ width: 120, height: 120 }}>
      <svg width="120" height="120" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} stroke={C.surface2} strokeWidth="8" fill="none" />
        <motion.circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          stroke={C.limeFg}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circ}
          initial={false}
          animate={{ strokeDashoffset: circ * (1 - score / 100) }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          transform="rotate(-90 60 60)"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span key={score} initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={{ fontFamily: serifFont, fontSize: 38, color: C.text, letterSpacing: -1.5, lineHeight: 1 }}>
          {score}
        </motion.span>
        <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5, marginTop: 2 }}>OF 100</span>
      </div>
    </div>
  );
}

/** Staged scan progress card (founder feedback #6). Shown only while
 *  `extractionStatus === 'running'`. Three stages with lime-accented
 *  active step + muted prior/next steps + a thin lime progress bar
 *  underneath. Card uses the dashboard's surface tier + thin 1px
 *  border + 2px radius — no shadows. Copy is plain Sill, not LLM
 *  output. Each stage is reached purely by elapsed time so the card
 *  feels alive even when the network is silent (Haiku runs 3-15s;
 *  proxy unblocker tier pushes the long tail). */
function ScanProgressCard({ stage }: { stage: 0 | 1 | 2 }): JSX.Element {
  const C = useC();
  const stages = [
    { label: 'Reading your site', detail: 'Fetching your homepage and following the canonical link.' },
    { label: 'Analyzing content', detail: 'Mapping copy to the agent-card fields agents query.' },
    { label: 'Drafting suggestions', detail: 'Composing per-field proposals for your review.' },
  ];
  const progressPct = stage === 0 ? 33 : stage === 1 ? 66 : 92;
  // Inline the Card surface (rather than wrap <Card>) so we can pin a
  // data-testid for the regression tests; the atom doesn't pass
  // through arbitrary props and we don't want to widen its surface
  // just for this view.
  return (
    <div
      className="relative mt-5 p-5"
      data-testid="scan-progress-card"
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 4,
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <RefreshCw
          size={11}
          color={C.limeFg}
          style={{ animation: 'spin 1.4s linear infinite' }}
        />
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
          }}
        >
          SCANNING · STAGE {stage + 1} OF 3
        </span>
      </div>
      <div className="space-y-2.5 mb-4">
        {stages.map((s, i) => {
          const active = i === stage;
          const done = i < stage;
          const upcoming = i > stage;
          return (
            <div
              key={s.label}
              className="flex items-start gap-3"
              data-testid={`scan-progress-stage-${i}`}
              data-active={active || undefined}
            >
              <div
                className="flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 2,
                  background: done
                    ? C.limeFg
                    : active
                      ? C.limeDim
                      : 'transparent',
                  border: `1px solid ${done || active ? C.limeFg : C.border}`,
                }}
              >
                {done ? (
                  <Check size={9} color={C.ink} strokeWidth={3} />
                ) : active ? (
                  <motion.div
                    animate={{ scale: [0.7, 1, 0.7] }}
                    transition={{ duration: 1.2, repeat: Infinity }}
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 1,
                      background: C.limeFg,
                    }}
                  />
                ) : null}
              </div>
              <div className="flex-1 min-w-0">
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 13,
                    color: upcoming ? C.muted : C.text,
                    fontWeight: active ? 500 : 400,
                    lineHeight: 1.4,
                  }}
                >
                  {s.label}
                  {active ? '…' : ''}
                </div>
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 11,
                    color: C.muted,
                    lineHeight: 1.5,
                    marginTop: 2,
                  }}
                >
                  {s.detail}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div
        className="relative"
        style={{
          height: 3,
          background: C.surface2,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <motion.div
          animate={{ width: `${progressPct}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          style={{
            height: '100%',
            background: C.limeFg,
            borderRadius: 2,
          }}
        />
      </div>
    </div>
  );
}

/** Resolve a field's display value, overlaying the in-progress edit
 *  from `pendingPreview` when the merchant is currently typing into
 *  that field's modal. Founder feedback #3: this is the live preview
 *  hook — the right-side panel shows what the merchant is typing
 *  even before they click Apply, so the "what agents will read"
 *  surface reflects intent in real time. Untrusted content is
 *  rendered as a JSX text node (auto-escaped); no new XSS surface. */
function resolveValue(
  fields: readonly VisibilityField[],
  pendingPreview: { field_id: string; value: string } | null,
  id: VisibilityField['id'],
): string | null {
  if (pendingPreview !== null && pendingPreview.field_id === id) {
    // Empty string ⇒ treat as null so the empty-state copy still
    // shows; this matches the way `isFieldPresent` reads the value.
    return pendingPreview.value.length > 0 ? pendingPreview.value : null;
  }
  return fields.find((f) => f.id === id)?.value ?? null;
}

/** Render the "what the browser shows the human" panel — parity with
 *  AgentView's "everything an agent reads" JSON-LD surface (founder
 *  feedback #5, 2026-06-18). Previously this only surfaced
 *  `name + description + shipping_zones + return_window_days` and the
 *  founder reasonably asked "does it surface ALL values?". It does
 *  now: every set `ProfileField` is rendered tastefully so the human
 *  preview reflects the WHOLE card, not a curated subset.
 *
 *  The structured-only fields (returns_policy_set, seller_*_url_set,
 *  catalog_search_eligible — ADR-0118 Slice A) are deliberately NOT
 *  rendered here. They're presence signals from `site_compliance` /
 *  `catalog_product`, set in the catalog / compliance views. They
 *  have no human-meaningful display value (the underlying text lives
 *  in the merchant's separate compliance editor, not on the card),
 *  so surfacing them as "✓ Returns policy" would be a tautology of
 *  the FIELDS list rather than a preview. AgentView prints them via
 *  the same `fields.map` loop because the agent reads the JSON-LD
 *  literal; the human reads natural-language prose.
 *
 *  Live-preview overlay (founder feedback #3) is honored on every
 *  resolved value via {@link resolveValue}, which folds the in-
 *  progress modal edit into the lookup before falling back to the
 *  saved card value. Untrusted strings render as JSX text nodes
 *  (React auto-escapes); no new XSS surface.
 */
function HumanView({
  fields,
  pendingPreview,
}: {
  fields: readonly VisibilityField[];
  pendingPreview: { field_id: string; value: string } | null;
}): JSX.Element {
  const C = useC();
  const val = (id: VisibilityField['id']): string | null =>
    resolveValue(fields, pendingPreview, id);
  const name = val('name') ?? 'Your store';
  const description = val('description');
  const shipping = val('shipping_zones');
  const returns = val('return_window_days');
  const priceRange = val('price_range');
  const currency = val('currency');
  const subscription = val('subscription_billing');
  const authoritative = val('agent_card_authoritative');
  // Any detail row visible? Drives the empty-state fallback so a
  // sparse profile still surfaces the "set those fields" hint.
  const hasAnyDetail =
    shipping !== null
    || returns !== null
    || priceRange !== null
    || currency !== null
    || subscription !== null
    || authoritative === 'true';

  return (
    <div style={{ background: C.paper, color: C.ink }} className="p-5 lg:p-7">
      <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 2, color: '#6B5F4F', marginBottom: 12 }}>WHAT THE BROWSER RENDERS</div>
      <div style={{ fontFamily: serifFont, fontSize: 28, lineHeight: 1, color: '#1A1410', letterSpacing: -1, marginBottom: 8 }} dir="auto">{name}</div>
      <div
        style={{ fontFamily: sansFont, fontSize: 13, color: '#6B5F4F', marginBottom: 18, lineHeight: 1.6, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
        dir="auto"
      >
        {description ?? '(No description set — agents have nothing to read about your business.)'}
      </div>
      <div className="space-y-1.5" data-testid="human-view-details">
        {priceRange !== null && (
          <div
            style={{ fontFamily: sansFont, fontSize: 12, color: '#867E72', lineHeight: 1.5 }}
            dir="auto"
            data-testid="human-detail-price_range"
          >
            ✓ Pricing: {priceRange}
            {currency !== null ? ` (${currency})` : ''}.
          </div>
        )}
        {priceRange === null && currency !== null && (
          <div
            style={{ fontFamily: sansFont, fontSize: 12, color: '#867E72', lineHeight: 1.5 }}
            data-testid="human-detail-currency"
          >
            ✓ Prices in {currency}.
          </div>
        )}
        {shipping !== null && (
          <div
            style={{ fontFamily: sansFont, fontSize: 12, color: '#867E72', lineHeight: 1.5, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
            dir="auto"
            data-testid="human-detail-shipping_zones"
          >
            ✓ Ships to {shipping}.
          </div>
        )}
        {returns !== null && (
          <div
            style={{ fontFamily: sansFont, fontSize: 12, color: '#867E72', lineHeight: 1.5 }}
            data-testid="human-detail-return_window_days"
          >
            ✓ {returns}-day returns.
          </div>
        )}
        {subscription !== null && (
          <div
            style={{ fontFamily: sansFont, fontSize: 12, color: '#867E72', lineHeight: 1.5, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
            dir="auto"
            data-testid="human-detail-subscription_billing"
          >
            ✓ Subscriptions: {subscription}.
          </div>
        )}
        {/* Authoritative flag — surface only when explicitly TRUE.
            "false" is the agent-card default ("we are NOT the
            authoritative source"); rendering "We do not claim
            authority" would be noise. */}
        {authoritative === 'true' && (
          <div
            style={{ fontFamily: sansFont, fontSize: 12, color: '#867E72', lineHeight: 1.5 }}
            data-testid="human-detail-agent_card_authoritative"
          >
            ✓ Authoritative source for agent answers.
          </div>
        )}
        {!hasAnyDetail && (
          <div style={{ fontFamily: sansFont, fontSize: 11, color: '#A89E89', lineHeight: 1.5, fontStyle: 'italic' }}>
            (No pricing, shipping, returns, or subscription details visible. Set those fields to surface them here.)
          </div>
        )}
      </div>
    </div>
  );
}

function AgentView({
  fields,
  pendingPreview,
}: {
  fields: readonly VisibilityField[];
  pendingPreview: { field_id: string; value: string } | null;
}): JSX.Element {
  const C = useC();
  const lines = fields
    .map((f) => {
      // Live preview overlay (founder feedback #3) — when the
      // merchant is currently typing into a field's modal, render
      // the in-progress value (marked with a comment) so the JSON-LD
      // preview reflects intent in real time. The value is rendered
      // as JSX text (auto-escaped); no new untrusted-output surface.
      const overlaid =
        pendingPreview !== null && pendingPreview.field_id === f.id;
      const effectiveValue = overlaid ? pendingPreview.value : f.value;
      const present = overlaid ? pendingPreview.value.length > 0 : f.present;
      if (!present) {
        return `\n  "${f.id}": null,  // missing${overlaid ? ' · pending edit' : ''}`;
      }
      const literal =
        typeof effectiveValue === 'string'
          && !effectiveValue.startsWith('[')
          ? `"${effectiveValue}"`
          : effectiveValue ?? 'null';
      return `\n  "${f.id}": ${literal},${overlaid ? '  // pending edit' : ''}`;
    })
    .join('');
  return (
    <div className="p-5 lg:p-7" style={{ background: C.surface2, borderLeft: `1px solid ${C.border}` }}>
      <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 2, color: C.muted, marginBottom: 12 }}>WHAT AGENTS PARSE</div>
      <pre style={{ fontFamily: monoFont, fontSize: 11, color: C.text, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
        {`{
  "@context": "https://schema.org",
  "@type": "Store",`}
        {lines}
        {`
}`}
      </pre>
    </div>
  );
}
